import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPrint, faSave, faTimes, faCopy, faPlus, faColumns, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { StatusBadge } from '../shared/StatusBadge';
import { ToggleSwitch } from '../shared/ToggleSwitch';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import SymbolBar from '../../components/product/SymbolBar';
import { useProductEdit } from '../../context/ProductEditContext';
import {
    ProductHeaderWrapper,
    NavigationBar,
    ProductTitleSection,
    ProductTitleRow,
    TitleLeft,
    TitleRight,
    BackButton,
    PrintButton,
    SplitViewButton,
    CategoryButton,
    NavButtonGroup,
    ProductHeadingTitle,
    ProductSymbolsRow,
    ButtonGroup,
} from './ProductHeader.styles';
import { SaveButton, CancelButton, CopyButton, NewProductButton } from './EditableFields.styles';
import { ProductDetail } from '../../types/ProductTypes';
import { formatDateTime } from '../../utils/dateFormatters';

interface ProductHeaderProps {
    title: string;
    subtitle?: React.ReactNode;
    isActive?: boolean;
    product: ProductDetail;
    onBackClick: () => void;
    backButtonTitle?: string;
    showPrintButton?: boolean;
    showStatusBadge?: boolean;
    showEditButtons?: boolean;
    showCopyButton?: boolean;
    showNewButton?: boolean;
    showSplitViewButton?: boolean;
    hideSplitViewButton?: boolean;
    hideBackButton?: boolean;
    isSplitView?: boolean;
    onSplitViewToggle?: () => void;
    showCategoryButton?: boolean;
    categoryProductName?: string;
    onCategoryClick?: () => void;
    onSave?: () => Promise<void>;
    onCancel?: () => void;
    onCopy?: () => void;
    onNewProduct?: () => void;
    isSaving?: boolean;
    hasUnsavedChanges?: boolean;
    children?: React.ReactNode;
}

const ProductHeader: React.FC<ProductHeaderProps> = ({
    title,
    subtitle,
    isActive = true,
    product,
    onBackClick,
    showPrintButton = true,
    showStatusBadge = true,
    showEditButtons = false,
    showCopyButton = false,
    showNewButton = false,
    showSplitViewButton = false,
    hideSplitViewButton = false,
    hideBackButton = false,
    isSplitView = false,
    onSplitViewToggle,
    showCategoryButton = false,
    categoryProductName,
    onCategoryClick,
    onSave,
    onCancel,
    onCopy,
    onNewProduct,
    isSaving = false,
    hasUnsavedChanges = false,
    children,
}) => {
    const { isInEditMode, setEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const { mergePendingChanges, pendingChanges } = useProductEdit();
    const showEditUI = canEdit && isInEditMode && showEditButtons;

    // Temporarily exit edit mode for printing, then restore it
    const handlePrint = useCallback(() => {
        if (!isInEditMode) {
            window.print();
            return;
        }
        setEditMode(false);
        // Let React re-render in read-only mode before opening print dialog
        requestAnimationFrame(() => {
            window.print();
            setEditMode(true);
        });
    }, [isInEditMode, setEditMode]);

    // Local state for isActive that can be toggled in edit mode
    const [localIsActive, setLocalIsActive] = useState(isActive);

    // Sync local state with prop when not in edit mode or when prop changes
    useEffect(() => {
        // Check if there's a pending isActive change
        if (pendingChanges?.isActive !== undefined) {
            setLocalIsActive(pendingChanges.isActive);
        } else {
            setLocalIsActive(isActive);
        }
    }, [isActive, pendingChanges?.isActive]);

    const handleToggleActive = (newValue: boolean) => {
        if (!showEditUI) return;
        setLocalIsActive(newValue);
        mergePendingChanges({ isActive: newValue });
    };

    return (
        <ProductHeaderWrapper>
            {/* Edit action buttons at the top */}
            {showEditUI && (
                <NavigationBar style={{ justifyContent: 'flex-end' }}>
                    <ButtonGroup>
                        {showNewButton && onNewProduct && (
                            <NewProductButton
                                onClick={onNewProduct}
                                disabled={hasUnsavedChanges}
                                title={hasUnsavedChanges ? 'Save or discard changes before creating' : 'Copies all details from this product category into a new product'}
                            >
                                <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
                                New Charterer Specific Product / Trade Name
                            </NewProductButton>
                        )}
                        {showCopyButton && onCopy && (
                            <CopyButton
                                onClick={onCopy}
                                disabled={hasUnsavedChanges}
                                title={hasUnsavedChanges ? 'Save or discard changes before copying' : 'Copy Charterer Specific Product / Trade Name'}
                            >
                                <FontAwesomeIcon icon={faCopy} style={{ marginRight: '5px' }} />
                                Copy Charterer Specific Product / Trade Name
                            </CopyButton>
                        )}
                        {onCancel && onSave && (
                            <>
                                <CancelButton onClick={onCancel} disabled={isSaving}>
                                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                                    Cancel
                                </CancelButton>
                                <SaveButton onClick={onSave} disabled={isSaving}>
                                    <FontAwesomeIcon icon={faSave} style={{ marginRight: '5px' }} />
                                    {isSaving ? <span style={{ fontSize: '0.7rem' }}>Saving...</span> : 'Save'}
                                </SaveButton>
                            </>
                        )}
                    </ButtonGroup>
                </NavigationBar>
            )}

            {/* Back and Print buttons */}
            <NavigationBar>
                <NavButtonGroup>
                    {!hideBackButton && (
                        <BackButton onClick={onBackClick}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </BackButton>
                    )}
                    {showSplitViewButton && !hideSplitViewButton && onSplitViewToggle && (
                        <SplitViewButton
                            onClick={onSplitViewToggle}
                            title={isSplitView ? 'Hide product list' : 'Show product list beside product'}
                            style={isSplitView ? { background: '#e2e6ea', borderColor: '#adb5bd' } : undefined}
                        >
                            <FontAwesomeIcon icon={faColumns} /> {isSplitView ? 'Hide List' : 'Split View'}
                        </SplitViewButton>
                    )}
                    {showCategoryButton && onCategoryClick && (
                        <CategoryButton
                            onClick={onCategoryClick}
                            title={categoryProductName ? `Go to category: ${categoryProductName}` : 'Go to product category'}
                        >
                            <FontAwesomeIcon icon={faLayerGroup} /> Product name
                        </CategoryButton>
                    )}
                </NavButtonGroup>
                <NavButtonGroup>
                    {showPrintButton && (
                        <PrintButton onClick={handlePrint}>
                            <FontAwesomeIcon icon={faPrint} /> Print
                        </PrintButton>
                    )}
                </NavButtonGroup>
            </NavigationBar>

            {/* Product title and symbols */}
            <ProductTitleSection>
                <ProductTitleRow>
                    <TitleLeft>
                        <ProductHeadingTitle $longTitle={title.length > 40}>
                            {title}
                            {subtitle}
                        </ProductHeadingTitle>
                    </TitleLeft>
                    <TitleRight>
                        {showStatusBadge && (
                            showEditUI ? (
                                <ToggleSwitch
                                    checked={localIsActive}
                                    onChange={handleToggleActive}
                                    size="small"
                                />
                            ) : (
                                <StatusBadge variant={localIsActive ? 'active' : 'inactive'}>
                                    {localIsActive ? 'Active' : 'Inactive'}
                                </StatusBadge>
                            )
                        )}
                        <SymbolBar product={product} canEdit={false} />
                    </TitleRight>
                </ProductTitleRow>
                <ProductSymbolsRow>
                    <span style={{ fontSize: '0.85em', color: '#666' }}>Last updated: {formatDateTime(product.lastUpdatedUtc)}</span>
                    {children}
                </ProductSymbolsRow>
            </ProductTitleSection>
        </ProductHeaderWrapper>
    );
};

export default ProductHeader;
